import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './BlogSearch.css';

const BlogSearch = ({ posts, className = '' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Extract all unique tags from posts
  const allTags = useMemo(() => {
    const tagSet = new Set();
    posts.forEach(post => {
      if (post.tags && Array.isArray(post.tags)) {
        post.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [posts]);

  // Filter posts based on search query and selected tags
  const filteredPosts = useMemo(() => {
    let filtered = posts;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(query) ||
        post.description.toLowerCase().includes(query) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    // Filter by selected tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(post =>
        post.tags && selectedTags.every(tag => post.tags.includes(tag))
      );
    }

    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [posts, searchQuery, selectedTags]);

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedTags([]);
  };

  const hasActiveFilters = searchQuery.trim() || selectedTags.length > 0;

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.blog-search')) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`blog-search ${className}`}>
      {/* Search Input */}
      <div className="search-input-container">
        <div className="search-input-wrapper">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="M21 21l-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchOpen(true)}
            className="search-input"
            aria-label="Search blog posts"
          />
          {hasActiveFilters && (
            <button 
              onClick={clearSearch}
              className="clear-search-btn"
              aria-label="Clear search"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Tag Filter */}
      <div className="tag-filter">
        <div className="tag-filter-header">
          <span className="tag-filter-label">Filter by tags:</span>
        </div>
        <div className="tag-list">
          {allTags.map(tag => (
            <button
              key={tag}
              onClick={() => handleTagToggle(tag)}
              className={`tag-btn ${selectedTags.includes(tag) ? 'selected' : ''}`}
              aria-pressed={selectedTags.includes(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Search Results */}
      {(isSearchOpen || hasActiveFilters) && (
        <div className="search-results">
          <div className="search-results-header">
            <span className="results-count">
              {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found
            </span>
            {hasActiveFilters && (
              <button onClick={clearSearch} className="clear-filters-btn">
                Clear filters
              </button>
            )}
          </div>
          
          <div className="search-results-list">
            {filteredPosts.length > 0 ? (
              filteredPosts.map(post => (
                <Link 
                  key={post.slug} 
                  to={`/blog/${post.slug}`}
                  className="search-result-item"
                  onClick={() => setIsSearchOpen(false)}
                >
                  <div className="search-result-content">
                    <h3 className="search-result-title">{post.title}</h3>
                    <p className="search-result-description">{post.description}</p>
                    <div className="search-result-meta">
                      <span className="search-result-date">
                        {new Date(post.date).toLocaleDateString()}
                      </span>
                      {post.tags && post.tags.length > 0 && (
                        <div className="search-result-tags">
                          {post.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="search-result-tag">{tag}</span>
                          ))}
                          {post.tags.length > 3 && (
                            <span className="search-result-tag-more">+{post.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="no-results">
                <p>No articles found matching your criteria.</p>
                <p>Try different keywords or clear your filters.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogSearch;
